import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Banknote, ArrowRight } from 'lucide-react';
import logo from '../assets/logo.png';

const SHOW_DELAY_MS = 1200;
const CATALOG_ANCHOR_ID = 'catalogo';

/**
 * Popup promocional: 10% de descuento pagando al contado en efectivo.
 * Aparece cada vez que se entra al home y baja hasta donde empiezan los productos.
 */
const PromoPopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Abrir cada vez que se entra al home; cerrar al salir de él
  useEffect(() => {
    if (location.pathname !== '/') {
      setIsOpen(false);
      return;
    }
    const timer = setTimeout(() => setIsOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const close = () => setIsOpen(false);

  // Cierra el popup y desplaza la página hasta el inicio del catálogo,
  // descontando la altura del header fijo para que no tape el título
  const goToCatalog = () => {
    close();
    setTimeout(() => {
      const target = document.getElementById(CATALOG_ANCHOR_ID);
      if (!target) return;
      const header = document.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }, 50);
  };

  // Bloquear scroll del fondo y cerrar con Escape
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Tarjeta */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-popup-titulo"
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
      >
        {/* Franja superior */}
        <div className="relative bg-[#2d3c5d] px-6 pt-6 pb-12 text-center overflow-hidden">
          {/* Detalles decorativos */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#eddacb]/10 pointer-events-none" />
          <div className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-white/5 pointer-events-none" />

          <button
            type="button"
            onClick={close}
            className="absolute top-3 right-3 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>

          <img
            src={logo}
            alt="Deco Home Sin Rival"
            className="relative h-14 sm:h-16 w-auto mx-auto filter brightness-110"
          />

          <span className="relative inline-flex items-center gap-1.5 mt-4 px-3.5 py-1.5 rounded-full bg-[#eddacb] text-[#2d3c5d] text-xs font-bold uppercase tracking-wide shadow-lg">
            <Banknote className="w-4 h-4" />
            Pago en efectivo
          </span>
        </div>

        {/* Cuerpo */}
        <div className="relative px-5 sm:px-6 pb-6 -mt-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-5 py-6 text-center">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              ¡Aprovechá un
            </p>

            <h2
              id="promo-popup-titulo"
              className="mt-1 text-[#2d3c5d] font-extrabold leading-none tracking-tight"
            >
              <span className="block text-6xl sm:text-7xl">10%</span>
              <span className="block mt-1 text-2xl sm:text-3xl">de descuento</span>
            </h2>

            <div className="w-14 h-1 mx-auto my-4 rounded-full bg-gradient-to-r from-[#2d3c5d] to-[#eddacb]" />

            <p className="text-base sm:text-lg font-semibold text-gray-800">
              pagando al contado en efectivo!
            </p>
            <p className="mt-1 text-xs text-gray-500">(No incluye ofertas)</p>

            <button
              type="button"
              onClick={goToCatalog}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#2d3c5d] hover:bg-[#1f2a3d] text-white font-semibold shadow-lg shadow-[#2d3c5d]/30 transition-all duration-300 hover:scale-[1.02] group"
            >
              Ver catálogo completo
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>

            <button
              type="button"
              onClick={close}
              className="mt-3 text-sm text-gray-500 hover:text-[#2d3c5d] transition"
            >
              Seguir navegando
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
